import { classes } from 'html-classes';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { BundleBadge } from '~/components/BundleBadge';
import Icon from '~/components/Icon/Icon';
import { MultibuyBadge } from '~/components/MultibuyBadge';
import { formatVolume, pluralizePcLabel } from '~/components/ProductCard/utils';
import { mainStore } from '~/stores';
import type { CartItem } from '~/stores/CartStore';
import type {
  CalculateStockBundleError,
  CalculateStockOfferError,
} from '~/stores/shared/CartStockChanges/interfaces';
import { isProductBundle } from '~/types/Product';
import { convertCartItemToProduct } from '~/utils/convertCartItemToProduct';
import { formatPriceWithCurrency } from '~/utils/formaters';

import styles from './ModalStockChanged.module.scss';

interface StockProductCardProps {
  product: CartItem;
  stockInfo: CalculateStockOfferError;
  bundleStockInfo?: Nullable<CalculateStockBundleError>;
  className?: string;
  isAgeRestriction?: boolean;
}

const StockProductCard = (props: StockProductCardProps) => {
  const {
    product: cartItem,
    stockInfo,
    bundleStockInfo,
    className,
    isAgeRestriction,
  } = props;

  const isBundle = isProductBundle(cartItem);

  const product = convertCartItemToProduct(cartItem);

  const { t } = useTranslation();

  const image = product.previewImageThumb;
  const lowestLevel = !isBundle ? product.getLowestMultibuyLevel() : null;
  const isMultiBuy = !isBundle && Boolean(lowestLevel);
  const price =
    !isMultiBuy && product.discountPrice
      ? product.discountPriceFormatted
      : product.priceFormatted;
  const oldPrice =
    !isMultiBuy && product.discountPrice ? product.priceFormatted : null;

  const getProductStock = () => {
    if (isBundle) {
      if (!bundleStockInfo) {
        return 0;
      }

      return bundleStockInfo.sellable >= 0 ? bundleStockInfo?.sellable : 0;
    }

    if (stockInfo.on_stock >= 0) {
      return stockInfo.on_stock;
    }

    return 0;
  };

  const onStock = getProductStock();

  const rating = product.ratingAverage ? (
    <div className="product-card__rated">
      <Icon type="star-fill" size={12} />
      {product.ratingAverage}
      {product.ratingMarksCount && <span> ({product.ratingMarksCount})</span>}
    </div>
  ) : (
    <div className={classes([styles.no_rating, 'product-card__no-rated'])}>
      <Icon type="star-fill" size={12} className={styles.no_rating__star} />
      {t('noRatingShort')}
    </div>
  );

  return (
    <div className={classes(['product-card', className])}>
      <div className="product-card__img-wrap">
        {isBundle && <BundleBadge className={styles.bundleBadge} />}
        {isMultiBuy && lowestLevel && (
          <MultibuyBadge
            value={lowestLevel.quantity}
            className={styles.multibuyCounter}
          />
        )}
        <div
          style={{ '--stock-product-image': `url(${image})` } as CSSProperties}
          className={classes([
            styles.productImage,
            (onStock <= 0 || isAgeRestriction) && styles.disabledImage,
          ])}
        />
      </div>
      <div className="product-card__body">
        <div className="product-card__info">
          {!company.hideReviews && rating}
          <p className="product-card__info-volume">
            {formatVolume(product.properties)}
          </p>
        </div>
        <div className={classes(['product-card__name', styles.productName])}>
          {product.name}
        </div>
        <div className="product-card__footer">
          <div className="product-card__price-wrap">
            <div
              className={classes([
                'product-card__price',
                styles.productPrice,
                oldPrice && 'good',
                isMultiBuy && styles.multibuyOldPrice,
              ])}
            >
              {formatPriceWithCurrency(+price)}
              {isMultiBuy ? ' / 1x' : ''}
            </div>
            {oldPrice && (
              <div className="product-card__price _old">
                {formatPriceWithCurrency(+oldPrice)}
              </div>
            )}
          </div>
          {isMultiBuy && lowestLevel && (
            <div className={styles.multibuyPriceWr}>
              <div className={styles.multibuyFromRule}>
                {t('phrases:fromPc', {
                  length: pluralizePcLabel(lowestLevel.quantity),
                })}
              </div>
              <div className="product-card__price good">
                {formatPriceWithCurrency(
                  mainStore.convertPenceToPounds(lowestLevel.unitPrice),
                )}
              </div>
            </div>
          )}
          <div className={styles.availableQty}>
            {!isAgeRestriction && (
              <span>
                {onStock > 0
                  ? t('phrases:availableStock', { count: onStock })
                  : t('outStock')}
              </span>
            )}
            <span className={styles.requestedQuantity}>
              {t('phrases:itemsCount', {
                count: stockInfo.requested_quantity,
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockProductCard;
